import { useRecentImages } from "../../../Providers/RecentImagesProvider";
import { Trans, useTranslation } from "react-i18next";
import useImageUpload from "../../../../../hooks/useImageUpload";
import React, { useEffect, useMemo, useRef } from "react";
import Container from "@material-ui/core/Container";
import Button from "@material-ui/core/Button";
import FilterIcon from "@material-ui/icons/FilterList";
import { Chip } from "@material-ui/core";
import UploadProgressDialog from "../../../Components/UploadProgressDialog";
import { makeStyles } from "@material-ui/core/styles";
import { useAPI } from "../../../../../lib/common";
import { Skeleton } from "@material-ui/lab";
import useRoutes, {
  getLinkComponentAttributes,
} from "../../../../hooks/useRoutes";
import { clientIsDesktop } from "../../../../../utils";
import useResizeObserver from "../../../../../hooks/useResizeObserver";

const useStyles = makeStyles((theme) => {
  return {
    filterButtonContainer: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      "&.one-button": {
        justifyContent: "flex-end",
      },
      "& button": {
        fontSize: "12px",
      },
    },
    // Placeholder, need to be here so we can reference it later
    categoriesListWithScrollBar: {},
    categoriesList: {
      listStyle: "none",
      margin: 0,
      padding: 0,
      overflow: "auto",
      whiteSpace: "nowrap",
      marginBottom: theme.spacing(2),
      "& > li": {
        display: "inline-block",
        marginLeft: theme.spacing(2),
      },
      "& > li:last-child": {
        marginRight: theme.spacing(2),
      },
      "&$categoriesListWithScrollBar": {
        paddingBottom: "10px",
      },
    },
    typeOptions: {
      backgroundColor: "white",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      boxShadow: "0px 2px 4px #00000029",
    },
    activeFilters: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      marginTop: theme.spacing(1),
      "& > li": {
        display: "inline-block",
        marginRight: theme.spacing(1),
      },
    },
    categoryButton: {
      paddingTop: "6px",
      paddingBottom: "6px",
      // Why does this need to be written like this?
      "&.MuiButton-root": {
        borderRadius: "4px",
      },
    },
  };
});

const TypeOptions = ({
  setFiltersOpen,
  category,
  categories,
  handleChangeCategory,
  disableFilters,
  filters,
  toggleFilterOption,
}) => {
  const { addImage } = useRecentImages();
  const { t } = useTranslation();
  const classes = useStyles();
  const onImageUploaded = (image) => {
    addImage({
      uuid: image.uuid,
      size: image.dimensions,
    });
  };
  const { isUploading, totalProgress, bind } = useImageUpload(onImageUploaded);
  const fileRef = useRef();
  const handleAddButtonClick = () => {
    fileRef.current.click();
  };

  const activeFilters = useMemo(() => {
    if (!filters) {
      return [];
    }
    const f = [];
    for (const filter of filters) {
      for (const option of filter.options) {
        if (option.checked) {
          f.push({ filter, option });
        }
      }
    }
    return f;
  }, [filters]);

  return (
    <div className={classes.typeOptions}>
      {categories && (
        <CardCategories
          handleChangeCategory={handleChangeCategory}
          category={category}
        />
      )}
      <Container>
        <div
          className={`${classes.filterButtonContainer} ${
            disableFilters ? "one-button" : ""
          }`}
        >
          {!disableFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFiltersOpen(true)}
              startIcon={<FilterIcon />}
            >
              <Trans>Filter selection</Trans>
            </Button>
          )}
          {/* Disabled as the functionality is not supported <Button
            variant="outlined"
            size="small"
            onClick={handleAddButtonClick}
            className={classes.addImagesButton}
            startIcon={<ImageIcon />}
          >
            <Trans>View with own pictures</Trans>
            <input
              type="file"
              ref={fileRef}
              {...bind}
              multiple={true}
              style={{ position: "absolute", left: "-9000px" }}
            />
          </Button>*/}
        </div>
        {activeFilters.length > 0 && (
          <ul className={classes.activeFilters}>
            {activeFilters.map((filter) => {
              const label = filter.filter.title + ": " + filter.option.name;
              return (
                <li key={label}>
                  <Chip
                    label={label}
                    size="small"
                    variant="outlined"
                    onDelete={() =>
                      toggleFilterOption(
                        filter.filter.type,
                        filter.option.value
                      )
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Container>
      {isUploading && <UploadProgressDialog totalProgress={totalProgress} />}
    </div>
  );
};

const CardCategories = ({ handleChangeCategory, category }) => {
  const [data, loading] = useAPI("/api/webshop/templates/cards/categories");
  const classes = useStyles();
  const listRef = useRef(null);
  const { size } = useResizeObserver(listRef);

  const scrollBars =
    clientIsDesktop() &&
    listRef.current &&
    listRef.current.scrollWidth > listRef.current.clientWidth;

  return (
    <ul
      id="categorylist"
      className={`${classes.categoriesList} ${
        scrollBars ? classes.categoriesListWithScrollBar : ""
      }`}
      ref={listRef}
    >
      {loading ? (
        <CardCategoriesSkeleton />
      ) : (
        <CardCategoriesList
          listRef={listRef}
          categories={data.categories}
          category={category}
        />
      )}
    </ul>
  );
};

const CardCategoriesList = ({ categories, category, listRef }) => {
  const { t } = useTranslation();
  const translatedCategories = categories
    .map((category) => {
      return {
        internalName: category,
        name: t(category),
      };
    })
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

  return (
    <>
      {translatedCategories.map((cat) => {
        return (
          <li key={cat.internalName}>
            <CardCategory
              category={cat}
              selected={cat.internalName == category}
              listRef={listRef}
            />
          </li>
        );
      })}
    </>
  );
};

const CardCategoriesSkeleton = () => {
  const numCategories = Array.from(Array(5).keys());
  return numCategories.map((i) => {
    return (
      <li key={i}>
        <Skeleton variant="rect" width={100} height={34} />
      </li>
    );
  });
};

const CardCategory = ({ category, selected, listRef }) => {
  const ref = useRef();
  const classes = useStyles();
  const routes = useRoutes();

  useEffect(() => {
    if (selected && ref.current) {
      const width = window.innerWidth;
      const size = ref.current.getBoundingClientRect();

      const left = ref.current.offsetLeft - width / 2 + size.width / 2;
      listRef.current.scrollLeft = left;
    }
  }, [selected]);

  const link = getLinkComponentAttributes(
    {
      path: "products.cards.category",
      variables: {
        type: "cards",
        category: category.internalName,
      },
    },
    routes,
    true
  );

  return (
    <Button
      ref={ref}
      variant="contained"
      size="small"
      classes={{ root: classes.categoryButton }}
      color={selected ? "secondary" : "primary"}
      {...link}
    >
      {category.name}
    </Button>
  );
};

export default TypeOptions;
